import type { IconName } from "@app/ui";

/**
 * Presentation helpers for the profile page: the monogram avatar and the
 * human reading of a session's user agent. All pure — no API, no state.
 */

/** Up to two initials: from the display name's words, else the email local part. */
export function initialsOf(displayName: string | null, email: string): string {
  const source = displayName?.trim() || (email.split("@")[0] ?? email);
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const letters = words.length >= 2 ? [words[0]![0], words[1]![0]] : [source[0], source[1]];
  return letters.filter(Boolean).join("").toLocaleUpperCase("en-US").slice(0, 2);
}

/** The name we greet the user with — never a bare "null". */
export function displayNameOf(displayName: string | null, email: string): string {
  return displayName?.trim() || (email.split("@")[0] ?? email);
}

export interface DeviceInfo {
  /** e.g. "Chrome · macOS"; falls back to the raw UA head when unrecognised. */
  label: string;
  icon: IconName;
}

// Deliberately a handful of substring probes rather than a UA-parsing dependency:
// this only has to help a user recognise their own device in a revoke list, and a
// wrong guess costs nothing (the IP and start time are shown beside it).
const BROWSERS: [RegExp, string][] = [
  [/\bEdg\//, "Edge"],
  [/\bOPR\/|\bOpera\b/, "Opera"],
  [/\bChrome\/|\bCriOS\//, "Chrome"],
  [/\bFirefox\/|\bFxiOS\//, "Firefox"],
  [/\bSafari\//, "Safari"],
];

const PLATFORMS: [RegExp, string, IconName][] = [
  [/\bAndroid\b/, "Android", "smartphone"],
  [/\biPhone\b|\biPod\b/, "iPhone", "smartphone"],
  [/\biPad\b/, "iPad", "smartphone"],
  [/\bMac OS X\b|\bMacintosh\b/, "macOS", "monitor"],
  [/\bWindows\b/, "Windows", "monitor"],
  [/\bCrOS\b/, "ChromeOS", "monitor"],
  [/\bLinux\b/, "Linux", "monitor"],
];

export function deviceFromUserAgent(ua: string | null, unknownLabel: string): DeviceInfo {
  if (!ua) return { label: unknownLabel, icon: "monitor" };

  const browser = BROWSERS.find(([re]) => re.test(ua))?.[1];
  const platform = PLATFORMS.find(([re]) => re.test(ua));
  const parts = [browser, platform?.[1]].filter(Boolean);
  // Nothing matched: show the head of the raw string, which is still more useful
  // to a developer or a curl client than "unknown".
  const label = parts.length ? parts.join(" · ") : ua.slice(0, 40);
  return { label, icon: platform?.[2] ?? "monitor" };
}
